/*
 * TOMME AI Cheesemonger widget.
 *
 * Bootstraps from window.TOMME_CM_CONFIG (set by the Liquid snippet or test
 * harness) and renders into any element matching the configured selector.
 *
 * Config keys:
 *   apiUrl   (string)  Full URL to the recommend endpoint.
 *   apiKey   (string)  Value sent as the x-tomme-api-key header.
 *   selector (string)  CSS selector for mount points. Default: ".tomme-cm-mount".
 *
 * Conversation context:
 *   The widget keeps a small in-memory transcript per mount for the current
 *   page session. Each request includes the recent turns plus the latest
 *   sessionId from the API so the cheesemonger can interpret follow-ups
 *   ("mild", "not goat", "for kids") in the context of the previous ask.
 *   Nothing is written to localStorage/sessionStorage/cookies — the history
 *   is cleared on reload.
 *
 * SECURITY: For the hidden MVP only. Anything in apiKey is visible to anyone
 * who views the storefront source. Replace with a Shopify App Proxy or
 * server-side relay before exposing this widget publicly.
 */
(function () {
  'use strict';

  var DEFAULT_SELECTOR = '.tomme-cm-mount';
  var PLACEHOLDER =
    'Ask for a cheese: soft and mild, sharp but not blue, something special for a gift...';
  var FOLLOWUP_PLACEHOLDER =
    'Reply with anything — “mild”, “for kids”, “not goat”, “make it Canadian”...';
  var TITLE = 'Ask the TOMME cheesemonger';
  var SUBTITLE =
    'Tell us what you’re after and we’ll point you at a few cheeses from the shop.';
  var SUBMIT_LABEL = 'Ask';
  var SUBMIT_LABEL_FOLLOWUP = 'Reply';
  var SUBMIT_LABEL_LOADING = 'Asking...';
  var RESET_LABEL = 'Start over';
  var LOADING_TEXT = 'Sniffing around the cheese cave...';
  var EMPTY_HINT = 'Try a few words about taste, texture, or the occasion.';
  var GENERIC_ERROR =
    'Sorry — something went sideways. Give it another try in a moment.';
  var MISSING_CONFIG_ERROR =
    'Widget is not configured. Set TOMME_CM_CONFIG before loading.';

  // How many turns (user + assistant combined) to send to the API. The
  // backend also clamps this, but we keep payloads small from the client.
  var MAX_HISTORY_TURNS = 6;
  var MAX_HISTORY_CONTENT_CHARS = 1200;

  function readConfig() {
    var cfg = (typeof window !== 'undefined' && window.TOMME_CM_CONFIG) || {};
    return {
      apiUrl: cfg.apiUrl || '',
      apiKey: cfg.apiKey || '',
      selector: cfg.selector || DEFAULT_SELECTOR,
    };
  }

  function createElement(tag, attrs, children) {
    var el = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (key) {
        var value = attrs[key];
        if (value == null) return;
        if (key === 'class') {
          el.className = value;
        } else if (key === 'text') {
          el.textContent = value;
        } else {
          el.setAttribute(key, value);
        }
      });
    }
    if (children) {
      children.forEach(function (child) {
        if (child) el.appendChild(child);
      });
    }
    return el;
  }

  function uniqueId(prefix) {
    return prefix + '-' + Math.random().toString(36).slice(2, 9);
  }

  function truncate(text, max) {
    if (typeof text !== 'string') return '';
    if (text.length <= max) return text;
    return text.slice(0, max);
  }

  function renderWidget(mount, config) {
    // Avoid double-mount if the snippet is included twice.
    if (mount.getAttribute('data-tomme-cm-ready') === '1') return;
    mount.setAttribute('data-tomme-cm-ready', '1');

    var inputId = uniqueId('tomme-cm-input');
    var statusId = uniqueId('tomme-cm-status');
    var transcriptId = uniqueId('tomme-cm-transcript');

    var root = createElement('div', { class: 'tomme-cm', role: 'region', 'aria-label': TITLE });

    var resetBtn = createElement('button', {
      class: 'tomme-cm__reset',
      type: 'button',
      text: RESET_LABEL,
      hidden: 'hidden',
      'aria-label': 'Start a new cheese conversation',
    });

    var header = createElement('div', { class: 'tomme-cm__header' }, [
      createElement('h2', { class: 'tomme-cm__title', text: TITLE }),
      resetBtn,
    ]);

    var subtitle = createElement('p', { class: 'tomme-cm__subtitle', text: SUBTITLE });

    var label = createElement('label', {
      class: 'tomme-cm__sr-only',
      for: inputId,
      text: 'Your cheese question',
    });

    var input = createElement('input', {
      class: 'tomme-cm__input',
      id: inputId,
      type: 'text',
      autocomplete: 'off',
      placeholder: PLACEHOLDER,
      'aria-describedby': statusId,
      maxlength: '500',
    });

    var submit = createElement('button', {
      class: 'tomme-cm__submit',
      type: 'submit',
      text: SUBMIT_LABEL,
    });

    var inputRow = createElement('div', { class: 'tomme-cm__input-row' }, [input, submit]);

    var hint = createElement('p', { class: 'tomme-cm__hint', text: EMPTY_HINT });

    var form = createElement('form', { class: 'tomme-cm__form', novalidate: 'novalidate' }, [
      label,
      inputRow,
      hint,
    ]);

    var status = createElement('div', {
      class: 'tomme-cm__status',
      id: statusId,
      role: 'status',
      'aria-live': 'polite',
    });

    var transcript = createElement('div', {
      class: 'tomme-cm__transcript',
      id: transcriptId,
      'aria-live': 'polite',
    });

    root.appendChild(header);
    root.appendChild(subtitle);
    root.appendChild(form);
    root.appendChild(status);
    root.appendChild(transcript);

    // Replace the mount contents (keeps any noscript fallback out of the way).
    mount.innerHTML = '';
    mount.appendChild(root);

    var inFlightController = null;

    // In-memory conversation state for this mount, this page load.
    // Never persisted. Cleared by "Start over" or page reload.
    var conversationHistory = []; // [{ role: 'user' | 'assistant', content: string }, ...]
    var lastSessionId = null;

    function hasHistory() {
      return conversationHistory.length > 0;
    }

    function updateInputAffordances() {
      if (hasHistory()) {
        input.setAttribute('placeholder', FOLLOWUP_PLACEHOLDER);
        submit.textContent = SUBMIT_LABEL_FOLLOWUP;
        resetBtn.hidden = false;
      } else {
        input.setAttribute('placeholder', PLACEHOLDER);
        submit.textContent = SUBMIT_LABEL;
        resetBtn.hidden = true;
      }
    }

    function appendTurn(role, content) {
      var turn = createElement('div', {
        class: 'tomme-cm__turn tomme-cm__turn--' + role,
      });
      var who = createElement('div', {
        class: 'tomme-cm__turn-role',
        text: role === 'user' ? 'You' : 'Cheesemonger',
      });
      var body = createElement('div', {
        class: 'tomme-cm__turn-body',
        text: content,
      });
      turn.appendChild(who);
      turn.appendChild(body);
      transcript.appendChild(turn);
    }

    function resetConversation() {
      conversationHistory = [];
      lastSessionId = null;
      transcript.innerHTML = '';
      clearStatus();
      input.value = '';
      updateInputAffordances();
      input.focus();
    }

    function setLoading(isLoading) {
      submit.disabled = isLoading;
      input.disabled = isLoading;
      resetBtn.disabled = isLoading;
      if (isLoading) {
        submit.textContent = SUBMIT_LABEL_LOADING;
        status.classList.remove('tomme-cm__status--error');
        status.innerHTML = '';
        var spinner = createElement('span', {
          class: 'tomme-cm__spinner',
          'aria-hidden': 'true',
        });
        var text = document.createTextNode(LOADING_TEXT);
        status.appendChild(spinner);
        status.appendChild(text);
      } else {
        submit.textContent = hasHistory() ? SUBMIT_LABEL_FOLLOWUP : SUBMIT_LABEL;
      }
    }

    function showError(message) {
      status.classList.add('tomme-cm__status--error');
      status.textContent = message || GENERIC_ERROR;
    }

    function clearStatus() {
      status.classList.remove('tomme-cm__status--error');
      status.textContent = '';
    }

    // Return a bounded copy of conversationHistory suitable for the API.
    function buildHistoryPayload() {
      if (!conversationHistory.length) return [];
      var bounded = conversationHistory.slice(-MAX_HISTORY_TURNS);
      return bounded.map(function (turn) {
        return {
          role: turn.role,
          content: truncate(turn.content, MAX_HISTORY_CONTENT_CHARS),
        };
      });
    }

    function submitQuery(query) {
      if (!config.apiUrl || !config.apiKey) {
        showError(MISSING_CONFIG_ERROR);
        return;
      }

      if (inFlightController) {
        inFlightController.abort();
      }
      inFlightController = typeof AbortController !== 'undefined' ? new AbortController() : null;

      // Optimistically render the user turn so the conversation reads as a transcript.
      appendTurn('user', query);

      setLoading(true);

      var body = { query: query };
      var historyPayload = buildHistoryPayload();
      if (historyPayload.length) body.conversationHistory = historyPayload;
      if (lastSessionId) body.priorSessionId = lastSessionId;

      var fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-tomme-api-key': config.apiKey,
        },
        body: JSON.stringify(body),
      };
      if (inFlightController) fetchOptions.signal = inFlightController.signal;

      fetch(config.apiUrl, fetchOptions)
        .then(function (response) {
          return response
            .json()
            .catch(function () {
              return {};
            })
            .then(function (data) {
              if (!response.ok) {
                var msg =
                  (data && data.error) ||
                  'Request failed (' + response.status + ').';
                throw new Error(msg);
              }
              return data;
            });
        })
        .then(function (data) {
          setLoading(false);
          clearStatus();
          var text = (data && data.answer) || '';
          if (!text) {
            showError('No answer came back. Try rephrasing your question.');
            return;
          }
          // Commit both sides to in-memory history only after a successful reply.
          conversationHistory.push({ role: 'user', content: query });
          conversationHistory.push({ role: 'assistant', content: text });
          // Trim to bound memory footprint on long sessions.
          if (conversationHistory.length > MAX_HISTORY_TURNS * 2) {
            conversationHistory = conversationHistory.slice(-MAX_HISTORY_TURNS * 2);
          }
          if (data && typeof data.sessionId === 'string') {
            lastSessionId = data.sessionId;
          }
          appendTurn('assistant', text);
          input.value = '';
          updateInputAffordances();
          input.focus();
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          setLoading(false);
          // The optimistic user turn already rendered; leave it so the customer
          // sees what they asked. They can retry with the same input.
          showError((err && err.message) || GENERIC_ERROR);
        });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var query = (input.value || '').trim();
      if (!query) {
        showError('Type a quick question first, then we’ll have a look.');
        input.focus();
        return;
      }
      submitQuery(query);
    });

    resetBtn.addEventListener('click', function () {
      resetConversation();
    });
  }

  function init() {
    var config = readConfig();
    var mounts = document.querySelectorAll(config.selector);
    if (!mounts.length) return;
    Array.prototype.forEach.call(mounts, function (mount) {
      renderWidget(mount, config);
    });
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }

  // Expose a tiny manual hook for theme editors / debugging.
  if (typeof window !== 'undefined') {
    window.TommeCheesemonger = { init: init };
  }
})();